import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { updatePageMeta, addStructuredData } from '../utils/seo';
import { pickHeroImage } from '../utils/heroImage';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Public listing page: /property/:slug
 *
 * This is a white-labeled microsite for the AGENT, not for Utah Realty Photos —
 * the palette comes from their configured primary/accent colors. So the design
 * system has to stay elegant with two arbitrary colors injected into it: agent
 * colors drive accents, rules and the contact band, never large fills that
 * could fight the photography.
 *
 * The gallery is the one place spending any boldness. A uniform grid treats a
 * 20-photo set like a contact sheet; a listing deserves hierarchy, so images
 * run in an editorial rhythm (one full-width, then a pair, repeating).
 */

// Editorial rhythm: 1 wide, then 2 side-by-side, repeating.
function groupIntoRows(images) {
  const rows = [];
  let i = 0;
  while (i < images.length) {
    if (rows.length % 2 === 0) {
      rows.push([images[i]]);
      i += 1;
    } else {
      rows.push(images.slice(i, i + 2));
      i += 2;
    }
  }
  return rows;
}

function getEmbedSrc(url) {
  try {
    if (url.includes('youtube.com/watch')) {
      const id = new URL(url).searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (url.includes('youtu.be/')) return `https://www.youtube.com/embed/${url.split('youtu.be/')[1].split('?')[0]}`;
    if (url.includes('youtube.com/shorts/')) return `https://www.youtube.com/embed/${url.split('shorts/')[1].split('?')[0]}`;
    if (url.includes('vimeo.com/')) return `https://player.vimeo.com/video/${url.split('vimeo.com/')[1].split('?')[0]}`;
    return null;
  } catch {
    return null;
  }
}

function PublicPropertyPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [inquiry, setInquiry] = useState({ name: '', email: '', phone: '', message: '', website: '' });
  const [inquiryState, setInquiryState] = useState('idle'); // idle | sending | sent | error
  const [inquiryError, setInquiryError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API_URL}/property/${slug}`);
        if (!cancelled) setPage(res.data.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.status === 404
            ? 'This listing page is no longer available.'
            : 'We could not load this listing.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPage();
    return () => { cancelled = true; };
  }, [slug]);

  const property = page?.property;
  const agent = page?.agent;
  const settings = page?.settings;
  const gallery = page?.gallery;

  const images = gallery?.images?.filter(i => i.mediaType !== 'video') || [];
  // Skips floor plans and documents — see utils/heroImage.js
  const coverImage = pickHeroImage(gallery, settings);
  const fullAddress = [property?.address, property?.city, property?.state, property?.zip].filter(Boolean).join(', ');

  // ── SEO ───────────────────────────────────────────────────────────────
  // Agents share these links, so each listing needs its own title, canonical
  // and structured data instead of inheriting the site-wide defaults.
  useEffect(() => {
    if (!property) return;
    // Avoid "…, Sandy, Sandy" — the address field usually includes the city.
    const addressLine = String(property.address || '').trim();
    const cityName = String(property.city || '').trim();
    const shortAddress =
      (cityName && !addressLine.toLowerCase().includes(cityName.toLowerCase())
        ? [addressLine, cityName].filter(Boolean).join(', ')
        : addressLine || cityName) || 'Property listing';
    const bits = [
      property.beds && `${property.beds} bed`,
      property.baths && `${property.baths} bath`,
      property.sqft && `${property.sqft} sq ft`
    ].filter(Boolean).join(' · ');
    const description = property.description
      ? String(property.description).slice(0, 155)
      : `${shortAddress}${bits ? ` — ${bits}` : ''}. Photos, video and 3D tour.`;

    updatePageMeta({
      title: `${shortAddress}${property.price ? ` — ${property.price}` : ''}`,
      description,
      canonical: `https://www.utahrealtyphotos.com/property/${slug}`
    });

    addStructuredData({
      '@context': 'https://schema.org',
      '@type': 'SingleFamilyResidence',
      name: shortAddress,
      description,
      ...(coverImage ? { photo: coverImage } : {}),
      ...(property.beds ? { numberOfBedrooms: property.beds } : {}),
      ...(property.baths ? { numberOfBathroomsTotal: property.baths } : {}),
      ...(property.yearBuilt ? { yearBuilt: property.yearBuilt } : {}),
      address: {
        '@type': 'PostalAddress',
        ...(property.address ? { streetAddress: property.address } : {}),
        ...(property.city ? { addressLocality: property.city } : {}),
        ...(property.state ? { addressRegion: property.state } : {}),
        ...(property.zip ? { postalCode: property.zip } : {}),
        addressCountry: 'US'
      }
    });
  }, [property, slug, coverImage]);

  // ── Lightbox keyboard control ─────────────────────────────────────────
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const step = useCallback((delta) => {
    setLightboxIndex(i => {
      if (i === null) return i;
      const next = i + delta;
      if (next < 0) return images.length - 1;
      if (next >= images.length) return 0;
      return next;
    });
  }, [images.length]);

  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIndex, closeLightbox, step]);

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!inquiry.name || !inquiry.email || !inquiry.message) {
      setInquiryError('Name, email and a message are all required.');
      setInquiryState('error');
      return;
    }
    setInquiryState('sending');
    setInquiryError('');
    try {
      const res = await axios.post(`${API_URL}/property/${slug}/inquire`, inquiry);
      if (res.data?.success) {
        setInquiryState('sent');
      } else {
        setInquiryError(res.data?.message || 'Could not send your message.');
        setInquiryState('error');
      }
    } catch (err) {
      setInquiryError(err.response?.data?.message || 'Could not send your message. Please try again.');
      setInquiryState('error');
    }
  };

  if (loading) return <div className="pp-state">Loading listing…</div>;
  if (error) {
    return (
      <div className="pp-state">
        <div>
          <p className="pp-state-msg">{error}</p>
          <a className="pp-state-link" href="https://www.utahrealtyphotos.com">Utah Realty Photos</a>
        </div>
      </div>
    );
  }
  if (!page) return null;

  const videos = gallery?.externalLinks?.filter(l => l.type === 'video') || [];
  const tours = gallery?.externalLinks?.filter(l => l.type === '3d_tour') || [];
  const floorPlans = gallery?.externalLinks?.filter(l => l.type === 'floor_plan') || [];
  const otherLinks = gallery?.externalLinks?.filter(l => !['video', '3d_tour', 'floor_plan'].includes(l.type)) || [];

  const primary = settings?.primaryColor || '#1a1a2e';
  const accent = settings?.accentColor || '#c9a96e';

  const stats = [
    property?.beds && { label: 'Beds', value: property.beds },
    property?.baths && { label: 'Baths', value: property.baths },
    property?.sqft && { label: 'Sq Ft', value: property.sqft },
    property?.lotSize && { label: 'Lot', value: property.lotSize },
    property?.yearBuilt && { label: 'Built', value: property.yearBuilt }
  ].filter(Boolean);

  const rows = groupIntoRows(images);
  const showPhotos = settings?.showPhotos !== false && images.length > 0;

  return (
    <div className="pp-root" style={{ '--pp-primary': primary, '--pp-accent': accent }}>
      <style>{PP_CSS}</style>

      <header className="pp-hero" style={coverImage ? { backgroundImage: `url(${coverImage})` } : { background: primary }}>
        <div className="pp-hero-scrim" />
        <div className="pp-hero-inner">
          {property?.status && <span className="pp-status">{property.status}</span>}
          {(property?.address || fullAddress) && <h1 className="pp-address">{property?.address || fullAddress}</h1>}
          {(property?.city || property?.state) && (
            <p className="pp-locality">{[property?.city, property?.state, property?.zip].filter(Boolean).join(', ')}</p>
          )}
          {property?.price && <p className="pp-price">{property.price}</p>}
        </div>
      </header>

      {stats.length > 0 && (
        <div className="pp-statbar">
          <div className="pp-statbar-inner">
            {stats.map((s) => (
              <div className="pp-stat" key={s.label}>
                <span className="pp-stat-value">{s.value}</span>
                <span className="pp-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <main className="pp-main">
        {property?.description && (
          <section className="pp-section">
            <p className="pp-eyebrow">About this home</p>
            <p className="pp-lede">{property.description}</p>
          </section>
        )}

        {property?.features?.length > 0 && (
          <section className="pp-section">
            <p className="pp-eyebrow">Features</p>
            <ul className="pp-features">
              {property.features.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </section>
        )}

        {showPhotos && (
          <section className="pp-section pp-section--wide">
            <p className="pp-eyebrow">Gallery</p>
            <div className="pp-gallery">
              {rows.map((row, ri) => (
                <div className={`pp-row ${row.length === 1 ? 'pp-row--single' : 'pp-row--pair'}`} key={ri}>
                  {row.map((img) => {
                    const idx = images.indexOf(img);
                    return (
                      <button
                        type="button"
                        key={img._id || idx}
                        className="pp-shot"
                        onClick={() => setLightboxIndex(idx)}
                        aria-label={`View photo ${idx + 1} of ${images.length}`}
                      >
                        <img src={img.url} alt={`${fullAddress || 'Property'} — photo ${idx + 1}`} loading={idx < 2 ? 'eager' : 'lazy'} />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </section>
        )}

        {settings?.showVideos !== false && videos.length > 0 && (
          <section className="pp-section pp-section--wide">
            <p className="pp-eyebrow">Video tour</p>
            <div className={videos.length > 1 ? 'pp-media-grid' : ''}>
              {videos.map((v, i) => {
                const src = getEmbedSrc(v.url);
                return src ? (
                  <div className="pp-embed" key={i}>
                    <iframe
                      src={src}
                      title={v.label || 'Video tour'}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <a className="pp-link-card" key={i} href={v.url} target="_blank" rel="noopener noreferrer">
                    {v.label || 'Watch the video tour'}
                  </a>
                );
              })}
            </div>
          </section>
        )}

        {settings?.show3DTour !== false && tours.length > 0 && (
          <section className="pp-section pp-section--wide">
            <p className="pp-eyebrow">3D walkthrough</p>
            {/* Tour hosting notice. The public property page is where a seller
                or buyer's agent meets the tour, and it is the surface most
                likely to be linked long after delivery — so it is where a
                "why is this gone?" question actually lands. Deliberately soft:
                a minimum with an honest tail, matching the terms, because the
                Matterport plan caps concurrent tours and removal is capacity-
                driven rather than scheduled. */}
            {gallery?.expiresAt && (
              <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '0 0 12px', lineHeight: 1.5 }}>
                Hosted through {new Date(gallery.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} at minimum; may be removed after that date as hosting capacity requires.
              </p>
            )}
            {tours.map((t, i) => {
              const isMatterport = t.url.includes('matterport.com') || t.url.includes('my.matterport');
              let embedUrl = t.url;
              if (isMatterport) {
                if (!embedUrl.includes('?')) embedUrl += '?play=1&qs=1';
                else if (!embedUrl.includes('play=')) embedUrl += '&play=1&qs=1';
              }
              return isMatterport ? (
                <div className="pp-embed" key={i}>
                  <iframe
                    src={embedUrl}
                    title={t.label || '3D tour'}
                    allow="xr-spatial-tracking; fullscreen; autoplay"
                    allowFullScreen
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                  />
                </div>
              ) : (
                <a className="pp-link-card" key={i} href={t.url} target="_blank" rel="noopener noreferrer">
                  {t.label || 'Open the 3D tour'}
                </a>
              );
            })}
          </section>
        )}

        {((settings?.showFloorPlan !== false && floorPlans.length > 0) || otherLinks.length > 0) && (
          <section className="pp-section">
            <p className="pp-eyebrow">Documents</p>
            <div className="pp-link-row">
              {settings?.showFloorPlan !== false && floorPlans.map((fp, i) => (
                <a className="pp-link-card pp-link-card--inline" key={`fp${i}`} href={fp.url} target="_blank" rel="noopener noreferrer">
                  {fp.label || 'Floor plan'}
                </a>
              ))}
              {otherLinks.map((l, i) => (
                <a className="pp-link-card pp-link-card--inline" key={`ol${i}`} href={l.url} target="_blank" rel="noopener noreferrer">
                  {l.label || 'View'}
                </a>
              ))}
            </div>
          </section>
        )}

        {property?.mlsNumber && <p className="pp-mls">MLS #{property.mlsNumber}</p>}
      </main>

      {settings?.showContactForm !== false && agent?.email && (
        <section className="pp-inquiry">
          <div className="pp-inquiry-inner">
            <p className="pp-eyebrow">Interested in this home?</p>
            <h2 className="pp-inquiry-title">Ask {agent?.name ? agent.name.split(' ')[0] : 'about this listing'}</h2>
            {inquiryState === 'sent' ? (
              <p className="pp-inquiry-sent">
                Thanks — your message is on its way{agent?.name ? ` to ${agent.name.split(' ')[0]}` : ''}. Expect a reply shortly.
              </p>
            ) : (
              <form className="pp-inquiry-form" onSubmit={submitInquiry} noValidate>
                {/* Honeypot — hidden from people, irresistible to bots */}
                <input
                  type="text" name="website" tabIndex="-1" autoComplete="off"
                  value={inquiry.website}
                  onChange={e => setInquiry({ ...inquiry, website: e.target.value })}
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                  aria-hidden="true"
                />
                <div className="pp-inquiry-row">
                  <input type="text" placeholder="Your name" value={inquiry.name}
                    onChange={e => setInquiry({ ...inquiry, name: e.target.value })} required />
                  <input type="email" placeholder="Email" value={inquiry.email}
                    onChange={e => setInquiry({ ...inquiry, email: e.target.value })} required />
                  <input type="tel" placeholder="Phone (optional)" value={inquiry.phone}
                    onChange={e => setInquiry({ ...inquiry, phone: e.target.value })} />
                </div>
                <textarea rows="4" placeholder="I'd like to schedule a showing…" value={inquiry.message}
                  onChange={e => setInquiry({ ...inquiry, message: e.target.value })} required />
                {inquiryState === 'error' && <p className="pp-inquiry-error">{inquiryError}</p>}
                <button type="submit" disabled={inquiryState === 'sending'}>
                  {inquiryState === 'sending' ? 'Sending…' : 'Send message'}
                </button>
              </form>
            )}
          </div>
        </section>
      )}

      {settings?.showAgentInfo !== false && agent?.name && (
        <section className="pp-agent">
          <div className="pp-agent-inner">
            {agent.photoUrl && <img className="pp-agent-photo" src={agent.photoUrl} alt={agent.name} />}
            <div className="pp-agent-body">
              <p className="pp-eyebrow pp-eyebrow--light">Presented by</p>
              <p className="pp-agent-name">{agent.name}</p>
              {agent.title && <p className="pp-agent-meta">{agent.title}</p>}
              {agent.brokerage && <p className="pp-agent-meta pp-agent-meta--dim">{agent.brokerage}</p>}
              <div className="pp-agent-actions">
                {agent.phone && <a className="pp-btn pp-btn--solid" href={`tel:${agent.phone}`}>{agent.phone}</a>}
                {agent.email && <a className="pp-btn" href={`mailto:${agent.email}`}>Email</a>}
                {agent.website && <a className="pp-btn" href={agent.website} target="_blank" rel="noopener noreferrer">Website</a>}
              </div>
            </div>
            {agent.brokerageLogo && <img className="pp-agent-logo" src={agent.brokerageLogo} alt={agent.brokerage || 'Brokerage'} />}
          </div>
        </section>
      )}

      {/* Photographer credit. Kept clearly subordinate to the agent's branding —
          this is their listing page — but visible enough to actually earn a
          referral, which a one-line grey footnote never did. */}
      <footer className="pp-credit">
        <img src="/logo.png" alt="Utah Realty Photos" className="pp-credit-logo" />
        <div className="pp-credit-text">
          <span className="pp-credit-label">Photography by</span>
          <a href="https://www.utahrealtyphotos.com" target="_blank" rel="noopener noreferrer" className="pp-credit-name">
            Utah Realty Photos
          </a>
          <span className="pp-credit-tag">Real estate photography across Utah County and the Wasatch Front</span>
        </div>
        <a href="https://www.utahrealtyphotos.com/book" target="_blank" rel="noopener noreferrer" className="pp-credit-cta">
          Book a shoot
        </a>
      </footer>

      {lightboxIndex !== null && images[lightboxIndex] && (
        <div className="pp-lightbox" role="dialog" aria-modal="true" aria-label="Property photo viewer">
          <button type="button" className="pp-lb-close" onClick={closeLightbox} aria-label="Close photo viewer">&times;</button>
          {images.length > 1 && (
            <button type="button" className="pp-lb-nav pp-lb-prev" onClick={() => step(-1)} aria-label="Previous photo">&#8249;</button>
          )}
          <img className="pp-lb-img" src={images[lightboxIndex].url} alt={`Property photo ${lightboxIndex + 1} of ${images.length}`} />
          {images.length > 1 && (
            <button type="button" className="pp-lb-nav pp-lb-next" onClick={() => step(1)} aria-label="Next photo">&#8250;</button>
          )}
          <p className="pp-lb-count">{lightboxIndex + 1} / {images.length}</p>
        </div>
      )}
    </div>
  );
}

/* Styles live in the component because this page renders standalone and needs
   media queries, hover and focus states that inline styles cannot express —
   which is exactly why the previous version read flat. */
const PP_CSS = `
.pp-root {
  --pp-ink: #16181d;
  --pp-muted: #6f7480;
  --pp-hairline: #e6e3dd;
  --pp-band: #faf9f6;
  background: #fff;
  color: var(--pp-ink);
  font-family: 'Work Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;
}
.pp-state {
  min-height: 70vh; display: flex; align-items: center; justify-content: center;
  font-family: 'Work Sans', sans-serif; color: #6f7480; text-align: center; padding: 2rem;
}
.pp-state-msg { font-size: 1.05rem; margin: 0 0 12px; }
.pp-state-link {
  color: #16181d; font-size: 0.85rem; letter-spacing: 0.08em;
  text-transform: uppercase; text-decoration: none; border-bottom: 1px solid currentColor;
}

.pp-hero {
  position: relative; min-height: 78vh; display: flex; align-items: flex-end;
  background-size: cover; background-position: center;
}
.pp-hero-scrim {
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(8,9,12,0.78) 0%, rgba(8,9,12,0.15) 55%, rgba(8,9,12,0.35) 100%);
}
.pp-hero-inner {
  position: relative; width: 100%; max-width: 1180px; margin: 0 auto;
  padding: 0 2rem 3.5rem; color: #fff;
}
.pp-status {
  display: inline-block; font-size: 0.68rem; letter-spacing: 0.18em; text-transform: uppercase;
  font-weight: 600; padding: 5px 12px; border: 1px solid rgba(255,255,255,0.5);
  border-radius: 2px; margin-bottom: 18px;
}
.pp-address {
  font-family: 'Playfair Display', Georgia, serif; font-weight: 500;
  font-size: clamp(2rem, 5.5vw, 3.75rem); line-height: 1.05;
  letter-spacing: -0.015em; margin: 0;
}
.pp-locality {
  font-size: clamp(0.9rem, 2vw, 1.05rem); letter-spacing: 0.14em; text-transform: uppercase;
  font-weight: 300; margin: 14px 0 0; opacity: 0.9;
}
.pp-price {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.4rem, 3vw, 2rem); margin: 20px 0 0; font-weight: 400;
}

.pp-statbar { border-bottom: 1px solid var(--pp-hairline); background: #fff; }
.pp-statbar-inner { max-width: 1180px; margin: 0 auto; padding: 0 2rem; display: flex; flex-wrap: wrap; }
.pp-stat {
  flex: 1 1 auto; min-width: 96px; padding: 22px 20px 20px;
  border-left: 1px solid var(--pp-hairline); text-align: center;
}
.pp-stat:first-child { border-left: 0; }
.pp-stat-value {
  display: block; font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.5rem; line-height: 1;
}
.pp-stat-label {
  display: block; margin-top: 7px; font-size: 0.66rem; letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--pp-muted);
}

.pp-main { max-width: 820px; margin: 0 auto; padding: 0 2rem; }
.pp-section { padding: 3.5rem 0; border-bottom: 1px solid var(--pp-hairline); }
.pp-section:last-of-type { border-bottom: 0; }
.pp-section--wide { max-width: 1180px; width: 1180px; margin-left: -180px; }
.pp-eyebrow {
  font-size: 0.68rem; letter-spacing: 0.2em; text-transform: uppercase;
  color: var(--pp-accent); font-weight: 600; margin: 0 0 1.4rem;
}
.pp-eyebrow--light { color: rgba(255,255,255,0.75); }
.pp-lede { font-size: 1.08rem; line-height: 1.85; color: #35393f; margin: 0; }
.pp-features { list-style: none; padding: 0; margin: 0; columns: 2; column-gap: 2.5rem; }
.pp-features li {
  break-inside: avoid; padding: 9px 0 9px 18px; position: relative;
  font-size: 0.95rem; color: #35393f; border-bottom: 1px solid var(--pp-hairline);
}
.pp-features li::before {
  content: ''; position: absolute; left: 0; top: 17px;
  width: 5px; height: 5px; border-radius: 50%; background: var(--pp-accent);
}

.pp-gallery { display: flex; flex-direction: column; gap: 10px; }
.pp-row { display: grid; gap: 10px; }
.pp-row--single { grid-template-columns: 1fr; }
.pp-row--pair { grid-template-columns: 1fr 1fr; }
.pp-shot {
  position: relative; padding: 0; border: 0; cursor: pointer; overflow: hidden;
  background: var(--pp-band); display: block; width: 100%;
}
.pp-row--single .pp-shot { aspect-ratio: 16 / 9; }
.pp-row--pair .pp-shot { aspect-ratio: 4 / 3; }
.pp-shot img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  transition: transform 0.6s cubic-bezier(0.2, 0.6, 0.2, 1);
}
.pp-shot:hover img { transform: scale(1.035); }
.pp-shot:focus-visible { outline: 2px solid var(--pp-accent); outline-offset: 3px; }

.pp-media-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(380px, 1fr)); gap: 14px; }
.pp-embed { position: relative; padding-bottom: 56.25%; background: #000; overflow: hidden; margin-bottom: 12px; }
.pp-embed iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
.pp-link-card {
  display: block; padding: 18px 22px; border: 1px solid var(--pp-hairline);
  text-decoration: none; color: var(--pp-ink); font-size: 0.95rem; font-weight: 500;
  transition: border-color 0.2s, color 0.2s;
}
.pp-link-card:hover { border-color: var(--pp-accent); color: var(--pp-accent); }
.pp-link-card--inline { display: inline-block; margin: 0 10px 10px 0; }
.pp-link-row { display: flex; flex-wrap: wrap; }
.pp-mls {
  font-size: 0.75rem; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--pp-muted); padding: 2rem 0 3rem; margin: 0;
}

.pp-inquiry { border-top: 1px solid var(--pp-hairline); background: #fff; padding: 3.5rem 2rem; }
.pp-inquiry-inner { max-width: 640px; margin: 0 auto; }
.pp-inquiry-title {
  font-family: 'Playfair Display', Georgia, serif; font-weight: 500;
  font-size: clamp(1.5rem, 3.5vw, 2rem); margin: 0 0 1.5rem; color: var(--pp-ink);
}
.pp-inquiry-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; }
.pp-inquiry-form input,
.pp-inquiry-form textarea {
  width: 100%; padding: 12px 14px; border: 1px solid var(--pp-hairline);
  border-radius: 6px; font-family: inherit; font-size: 0.95rem; color: var(--pp-ink);
  background: #fff; box-sizing: border-box;
}
.pp-inquiry-form textarea { margin-top: 10px; resize: vertical; }
.pp-inquiry-form input:focus,
.pp-inquiry-form textarea:focus { outline: 2px solid var(--pp-accent); outline-offset: 1px; border-color: transparent; }
.pp-inquiry-form button {
  margin-top: 14px; padding: 13px 30px; border: 0; border-radius: 6px;
  background: var(--pp-ink); color: #fff; font-family: inherit; font-weight: 600;
  font-size: 0.92rem; cursor: pointer; transition: opacity 0.2s;
}
.pp-inquiry-form button:hover { opacity: 0.88; }
.pp-inquiry-form button:disabled { opacity: 0.5; cursor: default; }
.pp-inquiry-error { color: #b91c1c; font-size: 0.88rem; margin: 10px 0 0; }
.pp-inquiry-sent {
  background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px;
  padding: 16px 18px; color: #15803d; font-size: 0.95rem; margin: 0;
}

.pp-agent { background: var(--pp-primary); color: #fff; padding: 4rem 2rem; }
.pp-agent-inner {
  max-width: 900px; margin: 0 auto; display: flex; gap: 2.5rem;
  align-items: center; flex-wrap: wrap;
}
.pp-agent-photo {
  width: 128px; height: 128px; border-radius: 50%; object-fit: cover;
  flex: none; border: 1px solid var(--pp-accent);
}
.pp-agent-body { flex: 1; min-width: 220px; }
.pp-agent-name { font-family: 'Playfair Display', Georgia, serif; font-size: 1.75rem; margin: 0 0 4px; }
.pp-agent-meta { margin: 0 0 2px; font-size: 0.95rem; opacity: 0.85; }
.pp-agent-meta--dim { opacity: 0.6; font-size: 0.85rem; }
.pp-agent-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 20px; }
.pp-btn {
  padding: 11px 22px; border: 1px solid rgba(255,255,255,0.35); color: #fff;
  text-decoration: none; font-size: 0.85rem; letter-spacing: 0.06em; font-weight: 500;
  transition: background 0.2s, border-color 0.2s;
}
.pp-btn:hover { background: rgba(255,255,255,0.12); }
.pp-btn--solid { background: var(--pp-accent); border-color: var(--pp-accent); }
.pp-btn--solid:hover { filter: brightness(1.08); background: var(--pp-accent); }
.pp-agent-logo { height: 54px; object-fit: contain; opacity: 0.9; }

.pp-credit {
  display: flex; align-items: center; justify-content: center; gap: 18px;
  flex-wrap: wrap; text-align: left;
  padding: 2.25rem 2rem; border-top: 1px solid var(--pp-hairline); background: var(--pp-band);
}
.pp-credit-logo { height: 40px; width: auto; opacity: 0.85; flex: none; }
.pp-credit-text { display: flex; flex-direction: column; gap: 2px; }
.pp-credit-label {
  font-size: 0.64rem; letter-spacing: 0.18em; text-transform: uppercase; color: var(--pp-muted);
}
.pp-credit-name {
  font-family: 'Playfair Display', Georgia, serif; font-size: 1.12rem;
  color: var(--pp-ink); text-decoration: none;
}
.pp-credit-name:hover { color: var(--pp-accent); }
.pp-credit-tag { font-size: 0.76rem; color: var(--pp-muted); }
.pp-credit-cta {
  flex: none; padding: 9px 18px; border: 1px solid var(--pp-ink); border-radius: 4px;
  font-size: 0.78rem; letter-spacing: 0.08em; text-transform: uppercase; font-weight: 600;
  color: var(--pp-ink); text-decoration: none; transition: background 0.2s, color 0.2s;
}
.pp-credit-cta:hover { background: var(--pp-ink); color: #fff; }
@media (max-width: 560px) {
  .pp-credit { flex-direction: column; text-align: center; gap: 12px; }
  .pp-credit-text { align-items: center; }
}

.pp-lightbox {
  position: fixed; inset: 0; z-index: 50000; background: rgba(8,9,12,0.96);
  display: flex; align-items: center; justify-content: center; padding: 4rem 1rem;
}
.pp-lb-img { max-width: 92vw; max-height: 86vh; object-fit: contain; }
.pp-lb-close {
  position: absolute; top: 18px; right: 22px; background: none; border: 0;
  color: rgba(255,255,255,0.75); font-size: 2.1rem; line-height: 1; cursor: pointer;
}
.pp-lb-close:hover { color: #fff; }
.pp-lb-nav {
  position: absolute; top: 50%; transform: translateY(-50%);
  background: none; border: 0; color: rgba(255,255,255,0.65);
  font-size: 3rem; line-height: 1; cursor: pointer; padding: 0 18px;
}
.pp-lb-nav:hover { color: #fff; }
.pp-lb-prev { left: 8px; }
.pp-lb-next { right: 8px; }
.pp-lb-count {
  position: absolute; bottom: 24px; left: 0; right: 0; text-align: center;
  color: rgba(255,255,255,0.6); font-size: 0.75rem; letter-spacing: 0.16em; margin: 0;
}
.pp-lightbox button:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }

@media (max-width: 1220px) {
  .pp-section--wide { max-width: 100%; width: 100%; margin-left: 0; }
}
@media (max-width: 720px) {
  .pp-hero { min-height: 62vh; }
  .pp-hero-inner { padding: 0 1.25rem 2.5rem; }
  .pp-main { padding: 0 1.25rem; }
  .pp-statbar-inner { padding: 0 0.5rem; }
  .pp-stat { min-width: 33%; padding: 16px 8px; }
  .pp-stat-value { font-size: 1.2rem; }
  .pp-section { padding: 2.5rem 0; }
  .pp-features { columns: 1; }
  .pp-row--pair { grid-template-columns: 1fr; }
  .pp-row--pair .pp-shot { aspect-ratio: 16 / 10; }
  .pp-agent { padding: 2.5rem 1.25rem; }
  .pp-agent-inner { gap: 1.5rem; }
  .pp-lb-nav { font-size: 2.2rem; padding: 0 8px; }
}
@media (prefers-reduced-motion: reduce) {
  .pp-shot img { transition: none; }
  .pp-shot:hover img { transform: none; }
}
`;

export default PublicPropertyPage;
